import { useEffect, useState } from "react";
import axios from "axios";
import { MessageCircle, Copy, Send, RefreshCw, Truck, Banknote, CheckCircle, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Billing = () => {
  const [activeTab, setActiveTab] = useState("unbilled");
  const [unbilledData, setUnbilledData] = useState([]);
  const [payoutData, setPayoutData] = useState([]);
  const [billedData, setBilledData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [messageData, setMessageData] = useState(null);
  const [generatingFor, setGeneratingFor] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [unbilled, payouts, billed] = await Promise.all([
        axios.get(`${API}/billing/unbilled`),
        axios.get(`${API}/billing/payouts`),
        axios.get(`${API}/billing/billed`),
      ]);
      setUnbilledData(unbilled.data);
      setPayoutData(payouts.data);
      setBilledData(billed.data);
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast.error("Failed to fetch billing data");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkBilled = async (customerPhone, billingType) => {
    try {
      await axios.post(`${API}/billing/mark-billed`, {
        customer_phone: customerPhone,
        billing_type: billingType,
      });
      toast.success(billingType === "payout"
        ? "Payout & its delivery charges moved to Billed tab"
        : "Marked as billed — moved to Billed tab");
      setMessageData(null);
      setExpandedCustomer(null);
      fetchAll();
    } catch (error) {
      toast.error("Failed to mark as billed");
    }
  };

  const handleGenerateMessage = async (customer) => {
    setGeneratingFor(customer.customer_phone);
    try {
      const response = await axios.post(`${API}/billing/generate-customer-message`, {
        customer_phone: customer.customer_phone,
        customer_name: customer.customer_name,
      });
      setMessageData(response.data);
      setExpandedCustomer(customer.customer_phone);
    } catch (error) {
      toast.error("Failed to generate message");
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleCopy = () => {
    if (messageData) {
      navigator.clipboard.writeText(messageData.message);
      toast.success("Copied to clipboard");
    }
  };

  const handleSendWhatsApp = () => {
    if (messageData) {
      window.open(messageData.whatsapp_link, "_blank");
    }
  };

  const tabs = [
    {
      id: "unbilled",
      label: "Unbilled Charges",
      icon: Truck,
      color: "bg-blue-600",
      inactiveColor: "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50",
      count: unbilledData.length,
    },
    {
      id: "payouts",
      label: "Payouts",
      icon: Banknote,
      color: "bg-orange-500",
      inactiveColor: "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50",
      count: payoutData.length,
    },
    {
      id: "billed",
      label: "Billed",
      icon: CheckCircle,
      color: "bg-emerald-600",
      inactiveColor: "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50",
      count: billedData.length,
    },
  ];

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="billing-title">Billing</h1>
          <p className="text-slate-500 mt-2">Manage delivery charges, payouts & billing</p>
        </div>
        <Button variant="outline" onClick={fetchAll} className="border-slate-300" data-testid="refresh-billing-btn">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-3" data-testid="billing-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setMessageData(null); setExpandedCustomer(null); }}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all ${
                isActive ? `${tab.color} text-white shadow-md` : tab.inactiveColor
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon size={18} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {activeTab === "unbilled" && <UnbilledTab
            data={unbilledData}
            expandedCustomer={expandedCustomer}
            setExpandedCustomer={setExpandedCustomer}
            onGenerateMessage={handleGenerateMessage}
            onMarkBilled={handleMarkBilled}
            generatingFor={generatingFor}
          />}
          {activeTab === "payouts" && <PayoutsTab
            data={payoutData}
            expandedCustomer={expandedCustomer}
            setExpandedCustomer={setExpandedCustomer}
            onGenerateMessage={handleGenerateMessage}
            onMarkBilled={handleMarkBilled}
            generatingFor={generatingFor}
          />}
          {activeTab === "billed" && <BilledTab data={billedData} />}
        </div>

        {/* Message Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-lg p-5 sticky top-6" data-testid="whatsapp-panel">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <MessageCircle size={16} className="text-green-600" />
              WhatsApp Message
            </h3>
            {!messageData ? (
              <div className="text-center py-8">
                <MessageCircle size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-400">Click "Generate Bill" on a customer to preview the message</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-h-80 overflow-y-auto">
                  <p className="text-sm text-slate-800 whitespace-pre-line font-mono leading-relaxed">
                    {messageData.message}
                  </p>
                </div>
                <div className="flex justify-center p-4 bg-white border border-slate-200 rounded-lg">
                  <img
                    src="https://customer-assets.emergentagent.com/job_1a9e1758-603d-4291-9897-b87c468206e6/artifacts/qsrw7n6o_photo_2025-06-12_14-17-25.jpg"
                    alt="UPI QR Code"
                    className="w-40 h-40 object-contain"
                    data-testid="upi-qr-code"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 border-slate-300" onClick={handleCopy} data-testid="copy-message-btn">
                    <Copy size={14} className="mr-2" />
                    Copy
                  </Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={handleSendWhatsApp} data-testid="send-whatsapp-btn">
                    <Send size={14} className="mr-2" />
                    Send
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Unbilled Delivery Charges Tab ─── */
const UnbilledTab = ({ data, expandedCustomer, setExpandedCustomer, onGenerateMessage, onMarkBilled, generatingFor }) => {
  if (data.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <CheckCircle size={40} className="mx-auto text-emerald-400 mb-3" />
        <p className="text-slate-500 font-medium">All delivery charges have been billed</p>
      </div>
    );
  }

  return (<>
    <p className="text-xs text-slate-500" data-testid="unbilled-note">Customers with payouts are billed from the Payouts tab — their delivery charges move to Billed automatically.</p>
    {data.map((customer) => {
    const isExpanded = expandedCustomer === customer.customer_phone;
    return (
      <div key={customer.customer_phone} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`unbilled-customer-${customer.customer_phone}`}>
        {/* Customer Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpandedCustomer(isExpanded ? null : customer.customer_phone)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg">
              {customer.customer_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{customer.customer_name}</p>
              <p className="text-xs text-slate-500">{customer.customer_phone} · {customer.orders.length} order{customer.orders.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xl font-extrabold text-blue-600">₹{customer.total_charges_with_expenses.toFixed(2)}</p>
            {isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
            <p className="text-xs font-semibold uppercase text-slate-500">Delivery Charges:</p>
            {customer.orders.map((o, idx) => (
              <div key={o.order_id} className="flex items-center justify-between text-sm px-2">
                <span className="text-slate-700">
                  {idx + 1}. "{o.order_details || o.order_id}"
                  {o.rider_name && <span className="text-xs text-slate-400 ml-1">via {o.rider_name}</span>}
                </span>
                <span className="font-medium text-slate-900">
                  {o.expense > 0
                    ? <span>₹{o.delivery_charge.toFixed(2)} + Exp ₹{o.expense.toFixed(2)} = <strong>₹{o.charge_plus_expense.toFixed(2)}</strong></span>
                    : <span>₹{o.delivery_charge.toFixed(2)}</span>
                  }
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm font-bold px-2 pt-2 border-t border-slate-200">
              <span>Total</span>
              <span className="text-blue-600">₹{customer.total_charges_with_expenses.toFixed(2)}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3">
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onGenerateMessage(customer); }}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={generatingFor === customer.customer_phone}
                data-testid={`generate-bill-${customer.customer_phone}`}
              >
                <FileText size={14} className="mr-1" />
                {generatingFor === customer.customer_phone ? "Generating..." : "Generate Bill"}
              </Button>
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onMarkBilled(customer.customer_phone, "delivery_charge"); }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`mark-billed-${customer.customer_phone}`}
              >
                <CheckCircle size={14} className="mr-1" />
                Mark Billed
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  })}
  </>);
};

/* ─── Payouts Tab ─── */
const PayoutsTab = ({ data, expandedCustomer, setExpandedCustomer, onGenerateMessage, onMarkBilled, generatingFor }) => {
  if (data.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <CheckCircle size={40} className="mx-auto text-emerald-400 mb-3" />
        <p className="text-slate-500 font-medium">No pending payouts</p>
      </div>
    );
  }

  return data.map((customer) => {
    const isExpanded = expandedCustomer === customer.customer_phone;
    return (
      <div key={customer.customer_phone} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`payout-customer-${customer.customer_phone}`}>
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => setExpandedCustomer(isExpanded ? null : customer.customer_phone)}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-lg">
              {customer.customer_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-slate-900">{customer.customer_name}</p>
              <p className="text-xs text-slate-500">{customer.customer_phone} · {customer.payout_orders.length} payout{customer.payout_orders.length > 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xl font-extrabold text-orange-600">₹{customer.balance.toFixed(2)}</p>
              <p className="text-xs text-slate-400">Balance to pay</p>
            </div>
            {isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-3">
            {/* Delivery Charges to deduct */}
            {customer.charge_orders.length > 0 && (
              <>
                <p className="text-xs font-semibold uppercase text-slate-500">Delivery Charges (to deduct):</p>
                {customer.charge_orders.map((o, idx) => (
                  <div key={o.order_id} className="flex items-center justify-between text-sm px-2">
                    <span className="text-slate-700">{idx + 1}. "{o.order_details || o.order_id}"</span>
                    <span className="font-medium text-red-600">
                      {o.expense > 0
                        ? <span>₹{o.delivery_charge.toFixed(2)} + Exp ₹{o.expense.toFixed(2)} = ₹{o.charge_plus_expense.toFixed(2)}</span>
                        : <span>₹{o.delivery_charge.toFixed(2)}</span>
                      }
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm font-bold px-2 text-red-600">
                  <span>Deduction Total</span>
                  <span>-₹{customer.total_deductions.toFixed(2)}</span>
                </div>
                <div className="border-t border-slate-200 my-1"></div>
              </>
            )}

            {/* Payouts */}
            <p className="text-xs font-semibold uppercase text-slate-500">Cash Collected (Payouts):</p>
            {customer.payout_orders.map((o, idx) => (
              <div key={o.order_id} className="flex items-center justify-between text-sm px-2">
                <span className="text-slate-700">
                  {idx + 1}. "{o.order_details || o.order_id}"
                  {o.rider_name && <span className="text-xs text-slate-400 ml-1">via {o.rider_name}</span>}
                </span>
                <span className="font-medium text-emerald-600">₹{o.cash_collected.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm font-bold px-2 text-emerald-600">
              <span>Payout Total</span>
              <span>₹{customer.total_payouts.toFixed(2)}</span>
            </div>

            {/* Balance */}
            <div className="bg-slate-900 rounded-lg p-3 mt-2">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-400">
                  <p>Payout: ₹{customer.total_payouts.toFixed(2)}</p>
                  {customer.total_deductions > 0 && <p>Deductions: -₹{customer.total_deductions.toFixed(2)}</p>}
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase font-semibold">Balance to Pay</p>
                  <p className={`text-2xl font-extrabold ${customer.balance >= 0 ? "text-white" : "text-red-400"}`}>
                    ₹{customer.balance.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onGenerateMessage(customer); }}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={generatingFor === customer.customer_phone}
                data-testid={`generate-payout-${customer.customer_phone}`}
              >
                <FileText size={14} className="mr-1" />
                {generatingFor === customer.customer_phone ? "Generating..." : "Generate Bill"}
              </Button>
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onMarkBilled(customer.customer_phone, "payout"); }}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`mark-payout-billed-${customer.customer_phone}`}
              >
                <CheckCircle size={14} className="mr-1" />
                Mark Billed
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  });
};

/* ─── Billed Tab ─── */
const BilledTab = ({ data }) => {
  if (data.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <FileText size={40} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 font-medium">No billed records yet</p>
      </div>
    );
  }

  return data.map((customer) => (
    <div key={customer.customer_phone} className="bg-white border border-slate-200 rounded-lg p-4 mb-3" data-testid={`billed-customer-${customer.customer_phone}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg">
            {customer.customer_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-slate-900">{customer.customer_name}</p>
            <p className="text-xs text-slate-500">{customer.customer_phone}</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
          BILLED
        </span>
      </div>
      <div className="space-y-1">
        {customer.orders.map((o) => (
          <div key={o.order_id} className="flex items-center justify-between text-sm px-2 py-1 bg-slate-50 rounded">
            <span className="text-slate-600 line-clamp-1">{o.order_details || o.order_id}</span>
            <div className="flex items-center gap-3 text-xs shrink-0">
              {o.charge_billed && <span className="text-blue-600 font-medium">DC: ₹{o.delivery_charge.toFixed(2)}</span>}
              {o.expense > 0 && <span className="text-red-500 font-medium">Exp: ₹{o.expense.toFixed(2)}</span>}
              {o.payout_billed && <span className="text-orange-600 font-medium">Payout: ₹{o.cash_collected.toFixed(2)}</span>}
              {o.rider_name && <span className="text-slate-400">via {o.rider_name}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  ));
};

export default Billing;
